/**
 * @fileoverview Footer page numbering settings card
 *
 * Purpose: Let a firm turn on "Page 2 of 5" style numbering across every
 * generated document, and pick the wording, alignment, and which pages carry
 * a number.
 *
 * Why it lives here and not in the letterhead cards: a letterhead footer is a
 * cropped image (or legacy extracted HTML) repeated verbatim on every matching
 * page, so it can't hold a value that changes per page. Numbering is a
 * separate footer element resolved at render time, and it's firm-wide rather
 * than per letterhead bundle — hence its own card.
 *
 * Dependencies:
 * - Convex: settings.branding.mutations.updateBrandingProfile
 * - `@/utils/branding/page-numbering` for the config shape + wording
 *
 * Used by:
 * - `src/routes/_app/_auth/dashboard/_layout.settings.branding.tsx`
 */

import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { Hash } from "lucide-react";
import { toast } from "sonner";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Switch } from "@/ui/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  DEFAULT_PAGE_NUMBERING,
  formatPageNumber,
  type PageNumberAlignment,
  type PageNumberFormat,
  type PageNumberingConfig,
} from "@/utils/branding/page-numbering";
import type { PageRule } from "@/utils/branding/resolve-letterhead";

/**
 * Pages a number can appear on. Narrower than {@link PageRule} because `none`
 * is deliberately absent — the toggle is how numbering is switched off, so
 * offering both would give two ways to say the same thing (and the mutation
 * validator rejects `none` for the same reason).
 */
type NumberedPageRule = Exclude<PageRule, "none">;

/** Fallback when a stored config has no usable rule. */
const FALLBACK_PAGE_RULE: NumberedPageRule = "notFirst";

const PAGE_RULE_OPTIONS: Array<{ value: NumberedPageRule; label: string }> = [
  { value: "notFirst", label: "All except first page" },
  { value: "all", label: "All pages" },
  { value: "firstOnly", label: "First page only" },
  { value: "oddOnly", label: "Odd pages only" },
  { value: "evenOnly", label: "Even pages only" },
];

const FORMAT_OPTIONS: Array<{ value: PageNumberFormat; label: string }> = [
  { value: "page_x_of_y", label: "Page 2 of 5" },
  { value: "page_x", label: "Page 2" },
  { value: "bare", label: "2" },
];

const ALIGNMENT_OPTIONS: Array<{
  value: PageNumberAlignment;
  label: string;
}> = [
  { value: "center", label: "Centre" },
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
];

/** Branding profile fields this card reads. */
export interface PageNumberingSource {
  pageNumbering?: Partial<PageNumberingConfig> | null;
}

/** The config as it's stored — see {@link NumberedPageRule}. */
type StoredPageNumbering = Omit<PageNumberingConfig, "pageRule"> & {
  pageRule: NumberedPageRule;
};

export function PageNumberingCard({
  orgId,
  profile,
  canEdit,
}: {
  orgId: Id<"organizations">;
  profile?: PageNumberingSource | null;
  canEdit: boolean;
}) {
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.branding.mutations.updateBrandingProfile,
    ),
  });

  // Fall back to the defaults field-by-field so a partially-written config
  // (older row, or a shape change) still renders a complete form.
  const saved = profile?.pageNumbering;
  const savedRule = saved?.pageRule;
  const config: StoredPageNumbering = {
    enabled: saved?.enabled ?? DEFAULT_PAGE_NUMBERING.enabled,
    format: saved?.format ?? DEFAULT_PAGE_NUMBERING.format,
    alignment: saved?.alignment ?? DEFAULT_PAGE_NUMBERING.alignment,
    pageRule:
      savedRule && savedRule !== "none" ? savedRule : FALLBACK_PAGE_RULE,
  };

  const save = async (patch: Partial<StoredPageNumbering>, message: string) => {
    try {
      await updateProfile({
        orgId,
        pageNumbering: { ...config, ...patch },
      });
      toast.success(message);
    } catch {
      toast.error("Failed to update page numbering.");
    }
  };

  return (
    <div className="border-t border-border/50 px-6 py-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Hash className="size-4 text-primary/40" />
            <h3 className="text-sm font-medium text-primary/80">
              Page numbers
            </h3>
          </div>
          <p className="mt-1 text-xs text-primary/50">
            Adds a numbered line at the bottom of generated documents, below
            your footer letterhead. Word documents use a live field, so the
            count stays right if the document is edited.
          </p>
        </div>
        <Switch
          checked={config.enabled}
          disabled={!canEdit}
          onCheckedChange={(checked) =>
            void save(
              { enabled: checked },
              checked ? "Page numbers on." : "Page numbers off.",
            )
          }
        />
      </div>

      {config.enabled && (
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <SettingSelect
            label="Format"
            value={config.format}
            options={FORMAT_OPTIONS}
            disabled={!canEdit}
            onChange={(value) =>
              void save({ format: value }, "Page number format updated.")
            }
          />
          <SettingSelect
            label="Alignment"
            value={config.alignment}
            options={ALIGNMENT_OPTIONS}
            disabled={!canEdit}
            onChange={(value) =>
              void save({ alignment: value }, "Page number alignment updated.")
            }
          />
          <SettingSelect
            label="Pages"
            value={config.pageRule}
            options={PAGE_RULE_OPTIONS}
            disabled={!canEdit}
            onChange={(value) =>
              void save({ pageRule: value }, "Page number pages updated.")
            }
          />
        </div>
      )}

      {config.enabled && (
        <div className="mt-4 rounded-lg border border-dashed border-border bg-secondary/30 px-4 py-3">
          <span className="text-[11px] uppercase tracking-wide text-primary/40">
            Preview
          </span>
          <div
            className="mt-1 text-xs text-primary/70"
            style={{ textAlign: config.alignment }}
          >
            {formatPageNumber(config.format, 2, 5)}
          </div>
        </div>
      )}
    </div>
  );
}

/** Labelled select used for each of the three numbering settings. */
function SettingSelect<T extends string>({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  disabled: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-primary/60">{label}</span>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(v) => onChange(v as T)}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="text-xs"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
